import { useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';

// Storefront
import Header from './components/Header';
import Footer from './components/Footer';
import MiniCart from './components/MiniCart';
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import About from './pages/About';
import Blog from './pages/Blog';
import Contact from './pages/Contact';
import TrackOrder from './pages/TrackOrder';
import ScrollToTop from './components/ScrollToTop';
import UserDashboard from './pages/UserDashboard';
import ReturnPolicy from './pages/ReturnPolicy';
import ShippingPolicy from './pages/ShippingPolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Admin
import { AuthProvider as AdminAuthProvider } from './admin/context/AuthContext';
import { ProductStoreProvider } from './admin/context/ProductStore';
import Login from './admin/pages/Login';
import AdminLayout from './admin/components/AdminLayout';
import Dashboard from './admin/pages/Dashboard';
import ProductsList from './admin/pages/ProductsList';
import AddProduct from './admin/pages/AddProduct';
import EditProduct from './admin/pages/EditProduct';
import Coupons from './admin/pages/Coupons';
import Settings from './admin/pages/Settings';
import StorefrontEditor from './admin/pages/StorefrontEditor';
import BlogsList from './admin/pages/BlogsList';
import BlogForm from './admin/pages/BlogForm';
import Orders from './admin/pages/Orders';

import WhatsAppWidget from './components/WhatsAppWidget';
import MaintenanceOverlay from './components/MaintenanceOverlay';

function StoreFront({ cart, setCart, addToCart, isCartOpen, setIsCartOpen }) {
  const cartCount = cart.reduce((s, i) => s + (i.qty || 1), 0);

  const updateQty = (id, delta) => {
    setCart(prev => prev.map(item => 
      item.id === id ? { ...item, qty: Math.max(1, (item.qty || 1) + delta) } : item
    ));
  };

  const removeItem = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  return (
    <>
      <MaintenanceOverlay />
      <Header cartCount={cartCount} onOpenCart={() => setIsCartOpen(true)} />
      <MiniCart 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
        cart={cart}
        updateQty={updateQty}
        removeItem={removeItem}
      />
      <Routes>
        <Route path="/"              element={<Home onAddToCart={addToCart} />} />
        <Route path="/shop"          element={<Shop onAddToCart={addToCart} />} />
        <Route path="/product/:slug" element={<ProductDetail onAddToCart={addToCart} />} />
        <Route path="/cart"          element={<Cart cart={cart} setCart={setCart} />} />
        <Route path="/checkout"      element={<Checkout cart={cart} setCart={setCart} />} />
        <Route path="/about"         element={<About />} />
        <Route path="/blog"          element={<Blog />} />
        <Route path="/blog/:slug"    element={<Blog />} />
        <Route path="/contact"       element={<Contact />} />
        <Route path="/track-order"   element={<TrackOrder />} />
        <Route path="/dashboard"     element={<UserDashboard />} />
        <Route path="/return-policy" element={<ReturnPolicy />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/wishlist"      element={<Shop onAddToCart={addToCart} />} />
        <Route path="/account"       element={<Home onAddToCart={addToCart} />} />
        <Route path="*"              element={<Home onAddToCart={addToCart} />} />
      </Routes>
      <WhatsAppWidget />
      <Footer />
    </>
  );
}

export default function App() {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, qty: (item.qty || 1) + 1 }
            : item
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
    setIsCartOpen(true);
  };

  return (
    <AuthProvider>
      <AdminAuthProvider>
        <ProductStoreProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Admin routes */}
              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index           element={<Dashboard />} />
                <Route path="products" element={<ProductsList />} />
                <Route path="orders"   element={<Orders />} />
                <Route path="add"      element={<AddProduct />} />
                <Route path="edit/:id" element={<EditProduct />} />
                <Route path="coupons"  element={<Coupons />} />
                <Route path="blogs"    element={<BlogsList />} />
                <Route path="blogs/add" element={<BlogForm />} />
                <Route path="blogs/edit/:id" element={<BlogForm />} />
                <Route path="settings" element={<Settings />} />
                <Route path="design"   element={<StorefrontEditor />} />
              </Route>

              {/* Storefront */}
              <Route path="/*" element={
                <StoreFront 
                  cart={cart} 
                  setCart={setCart} 
                  addToCart={addToCart} 
                  isCartOpen={isCartOpen} 
                  setIsCartOpen={setIsCartOpen} 
                />
              } />
            </Routes>
          </BrowserRouter>
        </ProductStoreProvider>
      </AdminAuthProvider>
    </AuthProvider>
  );
}
